import { createContext, useContext, useRef, useState, type ReactNode } from "react";
import { useProductExclusions, useRestoreProductExclusion } from "@acto/taskpane/core/data-access/product-exclusions";
import type { ProductExclusion } from "@acto/taskpane/core/domain";

export interface MeetingPrepProductExclusionsState {
  exclusions: ProductExclusion[];
  /** Resolves true once the server has lifted the exclusion. */
  restore: (id: string) => Promise<boolean>;
  restoringIds: ReadonlySet<string>;
  /** `id` changes on every `confirm`, even when the message repeats. */
  confirmation: { id: number; message: string } | null;
  confirm: (message: string) => void;
  dismissConfirmation: () => void;
}

const EMPTY: MeetingPrepProductExclusionsState = {
  exclusions: [],
  restore: () => Promise.resolve(false),
  restoringIds: new Set(),
  confirmation: null,
  confirm: () => undefined,
  dismissConfirmation: () => undefined,
};

const Context = createContext<MeetingPrepProductExclusionsState>(EMPTY);

export const useMeetingPrepProductExclusions = () => useContext(Context);

interface ProviderProps {
  clientId?: string;
  children: ReactNode;
}

export function MeetingPrepProductExclusionsProvider({ clientId, children }: ProviderProps) {
  const { data } = useProductExclusions(clientId);
  const { restoreProductExclusion } = useRestoreProductExclusion();
  const [restoringIds, setRestoringIds] = useState<ReadonlySet<string>>(new Set());
  const [confirmation, setConfirmation] = useState<MeetingPrepProductExclusionsState["confirmation"]>(null);
  const confirmationSeq = useRef(0);

  const confirm = (message: string) => setConfirmation({ id: ++confirmationSeq.current, message });

  const settle = (id: string) =>
    setRestoringIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });

  const restore = async (id: string) => {
    setRestoringIds((current) => new Set(current).add(id));
    try {
      return (await restoreProductExclusion(id)) != null;
    } finally {
      settle(id);
    }
  };

  return (
    <Context.Provider
      value={{
        exclusions: data ?? [],
        restore,
        restoringIds,
        confirmation,
        confirm,
        dismissConfirmation: () => setConfirmation(null),
      }}
    >
      {children}
    </Context.Provider>
  );
}
